import { React, useEffect, useState } from 'react';

// mui
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Autocomplete,
    TextField,
    Typography,
    Divider,
    TableContainer,
    Table,
    TableRow,
    TableBody,
    TableCell,
    Grid,
    Stack,
} from '@mui/material';

// components
import {
    AdminAutocomplete,
    optionsReport,
    optionsBlockExpires,
    optionsBlockReasons,
    optionsInactiveForced,
    optionsInactiveReasons,
    optionsSubscriptionStatus,
    optionsSubscriptionPlan,
    getUserStatusString,
} from '../';

import { def } from '@goldblack/shared';

function CommonOkDialog(props) {
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle sx={{ color: 'primary.main' }}>{props.title}</DialogTitle>
            <DialogContent>
                <DialogContentText>{props.desc}</DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button variant='outlined' onClick={props.onOK} autoFocus>
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function CommonYesNoDialog(props) {
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle>{props.title}</DialogTitle>
            <DialogContent>
                <DialogContentText>{props.desc}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' onClick={props.onYes}>
                    Yes
                </Button>
                <Button variant='outlined' onClick={props.onNo} autoFocus>
                    No
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function ReportDialog(props) {
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle>{props.title}</DialogTitle>
            <DialogContent>
                <DialogContentText>{props.desc}</DialogContentText>
                <Divider sx={{ my: 2 }}></Divider>
                <Autocomplete
                    sx={{ my: 1 }}
                    options={optionsReport}
                    getOptionLabel={(option) => option.label}
                    ListboxProps={{
                        style: {
                            backgroundColor: 'secondary.main',
                        },
                    }}
                    renderOption={(props, option) => (
                        <Box component='li' {...props}>
                            <Typography variant='body2'>{option.label}</Typography>
                        </Box>
                    )}
                    renderInput={(params) => <TextField {...params} label='reason' variant='standard' />}
                    onChange={props.onChange}
                />
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' disabled={props.okDisabled} onClick={props.onOK}>
                    Ok
                </Button>
                <Button variant='outlined' onClick={props.onCancel} autoFocus>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function BlockedDialog(props) {
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle sx={{ color: 'primary.main' }}>Your account is blocked</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ color: 'secondary.main' }}>
                    This block will expire on {props.expireDate}. Tell us if you think we made a mistake.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' disabled={props.okDisabled} onClick={props.onOK}>
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function InactiveDialog(props) {
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle sx={{ color: 'primary.main' }}>Your account is inactive</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ color: 'secondary.main' }}>
                    Your account has been forcibly inactive by administrator. Tell us if you think we made a mistake.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' disabled={props.okDisabled} onClick={props.onOK}>
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function LoiseUserStatusDialog(props) {
    const [expireValue, setExpireValue] = useState(-1);
    const [reasonValue, setReasonValue] = useState(-1);
    const [forcedValue, setForcedValue] = useState(-1);

    const onChangeExpire = (event, value) => {
        const expireValue = value ? value.code : -1;
        setExpireValue(expireValue);
    };

    const onChangeForced = (event, value) => {
        const forcedValue = value ? value.code : -1;
        setForcedValue(forcedValue);
    };

    const onChangeReason = (event, value) => {
        const reasonValue = value ? value.code : -1;
        setReasonValue(reasonValue);
    };

    const onClickOk = () => {
        if (props.onOK) {
            props.onOK(expireValue, forcedValue, reasonValue);
        }
    };

    useEffect(() => {
        setExpireValue(-1);
        setReasonValue(-1);
        setForcedValue(-1);
    }, []);

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle sx={{ color: 'primary.main' }}>Change Status</DialogTitle>
            <DialogContent>
                <Grid container spacing={0} direction='row' justifyContent='center' alignContent='center'>
                    <Typography sx={{ color: 'primary.main', mt: 0 }}>{props.user.name} &nbsp;</Typography>
                    <Typography sx={{ color: 'secondary.main', mb: 1 }}>
                        to {getUserStatusString(props.targetStatus)}?
                    </Typography>
                </Grid>
                <TableContainer>
                    <Table padding='normal'>
                        <TableBody>
                            {props.targetStatus === def.UserStatus.BLOCKED && (
                                <TableRow>
                                    <TableCell sx={{ width: '20%', borderBottom: 'none' }}>expire</TableCell>
                                    <TableCell sx={{ width: '80%', borderBottom: 'none' }} align='right'>
                                        <AdminAutocomplete options={optionsBlockExpires} onChange={onChangeExpire} />
                                    </TableCell>
                                </TableRow>
                            )}
                            {props.targetStatus === def.UserStatus.INACTIVE && (
                                <TableRow>
                                    <TableCell sx={{ width: '20%', borderBottom: 'none' }}>forced</TableCell>
                                    <TableCell sx={{ width: '80%', borderBottom: 'none' }}>
                                        <AdminAutocomplete options={optionsInactiveForced} onChange={onChangeForced} />
                                    </TableCell>
                                </TableRow>
                            )}
                            {props.targetStatus === def.UserStatus.BLOCKED && (
                                <TableRow>
                                    <TableCell sx={{ width: '20%', borderBottom: 'none' }}>reason</TableCell>
                                    <TableCell sx={{ width: '80%', borderBottom: 'none' }}>
                                        <AdminAutocomplete options={optionsBlockReasons} onChange={onChangeReason} />
                                    </TableCell>
                                </TableRow>
                            )}
                            {props.targetStatus === def.UserStatus.INACTIVE && (
                                <TableRow>
                                    <TableCell sx={{ width: '20%', borderBottom: 'none' }}>reason</TableCell>
                                    <TableCell sx={{ width: '80%', borderBottom: 'none' }}>
                                        <AdminAutocomplete options={optionsInactiveReasons} onChange={onChangeReason} />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' onClick={onClickOk}>
                    Ok
                </Button>
                <Button variant='outlined' onClick={props.onCancel}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function LoiseUserTypeDialog(props) {
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle sx={{ color: 'primary.main' }}>Change User Type</DialogTitle>
            <DialogContent>
                <Grid container spacing={0} direction='row' justifyContent='center' alignContent='center'>
                    <Typography sx={{ color: 'secondary.main', mr: 0 }}>
                        Do you want to change user type of &nbsp;
                    </Typography>
                    <Typography sx={{ color: 'primary.main', m: 0 }}>{props.user.name} &nbsp;</Typography>
                    <Typography sx={{ color: 'secondary.main', mr: 0 }}>
                        to {props.targetType === def.UserType.ADMIN ? 'Admin' : 'User'}?
                    </Typography>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' onClick={props.onOK}>
                    Ok
                </Button>
                <Button variant='outlined' onClick={props.onCancel}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function LoiseUserVerifiedDialog(props) {
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle sx={{ color: 'primary.main' }}>Change User Verified</DialogTitle>
            <DialogContent>
                <Grid container spacing={0} direction='row' justifyContent='center' alignContent='center'>
                    <Typography sx={{ color: 'secondary.main', mr: 0 }}>
                        Do you want to change verified of &nbsp;
                    </Typography>
                    <Typography sx={{ color: 'primary.main', m: 0 }}>{props.user.name}</Typography>
                    <Typography sx={{ color: 'secondary.main', mr: 0 }}>?</Typography>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' onClick={props.onOK}>
                    Ok
                </Button>
                <Button variant='outlined' onClick={props.onCancel}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function LoiseUserSubscriptionDialog(props) {
    const sub = props.user?.subscription ?? {};
    const [statusOption, setStatusOption] = useState(
        optionsSubscriptionStatus.find((o) => o.code === sub.status) ?? null,
    );
    const [planOption, setPlanOption] = useState(optionsSubscriptionPlan.find((o) => o.code === sub.plan) ?? null);
    const [expiresAt, setExpiresAt] = useState(() => {
        if (!sub.expires_at) return '';
        const d = new Date(sub.expires_at);
        return isNaN(d) ? '' : d.toISOString().slice(0, 16);
    });

    useEffect(() => {
        if (!props.open) return;
        const s = props.user?.subscription ?? {};
        setStatusOption(optionsSubscriptionStatus.find((o) => o.code === s.status) ?? null);
        setPlanOption(optionsSubscriptionPlan.find((o) => o.code === s.plan) ?? null);
        if (s.expires_at) {
            const d = new Date(s.expires_at);
            setExpiresAt(isNaN(d) ? '' : d.toISOString().slice(0, 16));
        } else {
            setExpiresAt('');
        }
    }, [props.open, props.user]);

    const onClickOk = () => {
        if (props.onOK) {
            props.onOK(
                statusOption?.code ?? null,
                planOption?.code ?? null,
                expiresAt ? new Date(expiresAt).getTime() : null,
            );
        }
    };

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth
            slotProps={{ paper: { elevation: 0, variant: 'outlined' } }}>
            <DialogTitle sx={{ color: 'primary.main' }}>Change Subscription</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Autocomplete
                        options={optionsSubscriptionStatus}
                        getOptionLabel={(o) => o.label}
                        value={statusOption}
                        onChange={(_, v) => setStatusOption(v)}
                        renderInput={(params) => <TextField {...params} label='status' variant='standard' />}
                    />
                    <Autocomplete
                        options={optionsSubscriptionPlan}
                        getOptionLabel={(o) => o.label}
                        value={planOption}
                        onChange={(_, v) => setPlanOption(v)}
                        renderInput={(params) => <TextField {...params} label='plan' variant='standard' />}
                    />
                    <TextField
                        label='expires at'
                        type='datetime-local'
                        variant='standard'
                        value={expiresAt}
                        onChange={(e) => setExpiresAt(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{
                            '& input::-webkit-calendar-picker-indicator': {
                                filter: 'brightness(0) saturate(100%) invert(91%) sepia(29%) saturate(421%) hue-rotate(349deg)',
                                cursor: 'pointer',
                            },
                        }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' onClick={onClickOk}>
                    Ok
                </Button>
                <Button variant='outlined' onClick={props.onCancel}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function LoiseUserDeleteDialog(props) {
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth
            slotProps={{
                paper: {
                    elevation: 0,
                    variant: 'outlined',
                },
            }}>
            <DialogTitle sx={{ color: 'primary.main' }}>Delete User</DialogTitle>
            <DialogContent>
                <Grid container spacing={0} direction='row' justifyContent='center' alignContent='center'>
                    <Typography sx={{ color: 'secondary.main', mr: 0 }}>Do you want to delete &nbsp;</Typography>
                    <Typography sx={{ color: 'primary.main', m: 0 }}>{props.user.username}</Typography>
                    <Typography sx={{ color: 'secondary.main', mr: 0 }}>?</Typography>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' onClick={props.onOK}>
                    Ok
                </Button>
                <Button variant='outlined' onClick={props.onCancel}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function LaunchInstanceDialog({ open, onClose, awsLaunchTemplates = [], onStart }) {
    const [template, setTemplate] = useState(null);

    useEffect(() => {
        if (!open) return;
        setTemplate(awsLaunchTemplates[0] ?? null);
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth slotProps={{ paper: { elevation: 0, variant: 'outlined' } }}>
            <DialogTitle sx={{ color: 'primary.main' }}>Launch Instance</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Autocomplete
                        options={awsLaunchTemplates}
                        getOptionLabel={(t) => `${t.name} · ${t.region ?? 'us-east-1'}`}
                        value={template}
                        onChange={(_, val) => setTemplate(val)}
                        renderInput={(params) => (
                            <TextField {...params} label='AWS launch template' variant='standard' />
                        )}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    variant='outlined'
                    disabled={!template}
                    onClick={() => onStart(template.id, template.name, template.region ?? 'us-east-1')}>
                    Start
                </Button>
                <Button variant='outlined' onClick={onClose}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export {
    CommonOkDialog,
    CommonYesNoDialog,
    ReportDialog,
    BlockedDialog,
    InactiveDialog,
    LoiseUserStatusDialog,
    LoiseUserTypeDialog,
    LoiseUserVerifiedDialog,
    LoiseUserSubscriptionDialog,
    LoiseUserDeleteDialog,
    LaunchInstanceDialog,
};
